import * as Eden from "src/eden/content";
import { SaberGameSnapshot } from "src/utils/sabertooth-client";
import { PlayerMap, playerFullName } from "./game-stream-helpers";

// Counting stats we cross-check between our sabertooth-derived box score and GameChanger's official
// per-game book. All integers, so any difference is a real disagreement, not a rounding artifact
// (rate stats like AVG/OPS are intentionally excluded). The first six are the visible batting
// columns; 2B/3B/HR live in the notes line but are worth flagging too.
export const DIFF_STATS = ["AB", "R", "H", "RBI", "BB", "SO", "2B", "3B", "HR"] as const;

export interface StatDiscrepancy {
  playerId: string;
  playerName: string;
  stat: string;
  box: number; // our sabertooth-derived value
  gc: number; // GameChanger's official value
}

export interface BoxScoreDiff {
  discrepancies: StatDiscrepancy[];
  // Lookup for cell highlighting, keyed `${playerId}:${STAT}`.
  byCell: Map<string, { box: number; gc: number }>;
}

type OffenseLike = Record<string, unknown> | undefined;

// GameChanger's contract lowercases most counting keys (ab, h, r…) while sabertooth uses uppercase
// (AB, H, 2B…). Read tolerant of either casing so the same stat lines up on both sides.
function readStat(offense: OffenseLike, key: string): number {
  if (!offense) return 0;
  for (const k of [key, key.toUpperCase(), key.toLowerCase()]) {
    const v = offense[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return 0;
}

// Compares our team's batting lines (sabertooth) against GameChanger's official per-game player
// stats. Only players present in BOTH sources are compared; GC's player-stats endpoint is scoped to
// our team, so this never covers the opponent panel. Returns no discrepancies until both the replay
// state and GC's stats are available.
export function compareBoxScore(
  state: SaberGameSnapshot | null,
  teamId: string,
  gcEventStats: Eden.EventStats | undefined,
  players: PlayerMap
): BoxScoreDiff {
  const discrepancies: StatDiscrepancy[] = [];
  const byCell = new Map<string, { box: number; gc: number }>();

  const saberPlayers = state?.teamStats[teamId]?.players;
  const gcPlayers = gcEventStats?.player_stats?.players;
  if (!saberPlayers || !gcPlayers) return { discrepancies, byCell };

  for (const [pid, saberLine] of Object.entries(saberPlayers)) {
    const gcLine = gcPlayers[pid];
    if (!gcLine) continue; // can't compare a player GameChanger doesn't list (e.g. didn't appear)
    const saberOff = saberLine?.stats?.offense as OffenseLike;
    const gcOff = gcLine?.stats?.offense as OffenseLike;
    for (const s of DIFF_STATS) {
      const box = readStat(saberOff, s);
      const gc = readStat(gcOff, s);
      if (box !== gc) {
        discrepancies.push({ playerId: pid, playerName: playerFullName(players[pid]), stat: s, box, gc });
        byCell.set(`${pid}:${s}`, { box, gc });
      }
    }
  }
  return { discrepancies, byCell };
}
